export interface QueryParams {
  offset?: number;
  limit?: number;
  search?: string;
  searchTerm?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  isExport?: boolean;
  [key: string]: any;
}

/**
 * Formats a query parameter key based on case requirements.
 * Helps reduce cognitive complexity in parameter building.
 */
const formatParamKey = (
  key: string,
  usePascalCase: boolean,
  pascalOverride?: string
) => {
  if (!usePascalCase) return key;
  return pascalOverride || key.charAt(0).toUpperCase() + key.slice(1);
};

/**
 * Builds URLSearchParams from a QueryParams object.
 * Supports PascalCase and custom overrides.
 */
export const buildQueryParams = (
  params: QueryParams,
  usePascalCase = true
): URLSearchParams => {
  const searchParams = new URLSearchParams();
  const {
    offset,
    limit,
    search,
    searchTerm,
    sortBy,
    sortDir,
    isExport,
    ...rest
  } = params;

  if (isExport) {
    searchParams.set(
      formatParamKey('isExport', usePascalCase, 'isExport'),
      'true'
    );
  } else {
    if (offset !== undefined)
      searchParams.set(
        formatParamKey('offset', usePascalCase, 'Offset'),
        String(offset)
      );
    if (limit !== undefined)
      searchParams.set(
        formatParamKey('limit', usePascalCase, 'Limit'),
        String(limit)
      );
  }

  if (sortBy)
    searchParams.set(formatParamKey('sortBy', usePascalCase, 'SortBy'), sortBy);
  if (sortDir)
    searchParams.set(
      formatParamKey('sortDir', usePascalCase, 'SortDir'),
      sortDir
    );
  if (search)
    searchParams.set(
      formatParamKey('searchField', usePascalCase, 'SearchField'),
      search
    );
  if (searchTerm)
    searchParams.set(
      formatParamKey('searchTerm', usePascalCase, 'SearchTerm'),
      searchTerm
    );

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.set(formatParamKey(key, usePascalCase), String(value));
    }
  });

  return searchParams;
};
